import * as PIXI from 'pixi.js';
import type { BoardCell, Player, PropertyOwnership } from '../types';
import { ASSETS } from '../data/assets';

// ============ pixi v7 兼容：手动绘制星形（v6 的 drawStar 在 v7 中已移除） ============
function drawStar(g: PIXI.Graphics, cx: number, cy: number, points: number, outerRadius: number, innerRadius: number) {
  const step = Math.PI / points;
  g.moveTo(cx, cy - outerRadius);
  for (let i = 0; i < points * 2; i++) {
    const angle = i * step - Math.PI / 2;
    const r = i % 2 === 0 ? outerRadius : innerRadius;
    g.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
  }
  g.closePath();
}

// ============ 配色方案 · 卡通休闲风 ============
const COLORS = {
  streetBg: {
    '像素小镇': 0xB8E6C8,
    '蒸汽都市': 0xFFD4C4,
    '霓虹城区': 0xD4CCF8,
    '云端天际': 0xFFE8C0,
  } as Record<string, number>,
  streetAccent: {
    '像素小镇': 0x5CB88C,
    '蒸汽都市': 0xFF8C69,
    '霓虹城区': 0xA29BFE,
    '云端天际': 0xF4A950,
  } as Record<string, number>,
  specialBg: {
    start: 0xB8E6C8,
    jail: 0xF0E0D0,
    chance: 0xE8DCF8,
    event: 0xFFE0D0,
    tax: 0xFFE8C0,
    auction: 0xD8ECF8,
    reverseShop: 0xD4F0F4,
    casino: 0xFFDCE8,
  } as Record<string, number>,
  specialAccent: {
    start: 0x5CB88C,
    jail: 0xC4B5A5,
    chance: 0xA29BFE,
    event: 0xFF8C69,
    tax: 0xF4A950,
    auction: 0x6CB4EE,
    reverseShop: 0x5CBDB8,
    casino: 0xFF6B8A,
  } as Record<string, number>,
  playerColors: [0xFF6B6B, 0x7EC8A4, 0x6CB4EE, 0xF4A950],
  playerDark: [0xE55555, 0x5AB88C, 0x4A9AD4, 0xD49030],
  boardBg: 0xFFECD2,
  boardOuter: 0xFFF5EE,
  cellNeutral: 0xFFFFFF,
  ownershipGlow: 0xFFF5EE,
  goldText: 0xE8913A,
  white: 0xFFFFFF,
};

// ============ 卡通地形图标 (size=38, 格子155以内) ============
function drawTerrainIcon(g: PIXI.Graphics, type: string, street: string | undefined, cx: number, cy: number): void {
  const s = 36;

  switch (type) {
    case 'start': {
      // 🚩 可爱旗帜：圆杆 + 三角旗 + 顶球
      g.lineStyle(3.5, 0xE8913A, 1);
      g.moveTo(cx, cy - s * 0.85);
      g.lineTo(cx, cy + s * 0.55);
      // 旗帜三角形
      g.beginFill(0x7EC8A4, 0.95);
      g.moveTo(cx, cy - s * 0.85);
      g.lineTo(cx + s * 0.78, cy - s * 0.5);
      g.lineTo(cx + s * 0.1, cy - s * 0.38);
      g.lineTo(cx + s * 0.65, cy - s * 0.15);
      g.lineTo(cx, cy - s * 0.1);
      g.closePath();
      g.endFill();
      // 顶球
      g.beginFill(0xFFD700);
      g.drawCircle(cx, cy - s * 0.9, 4.5);
      g.endFill();
      // 小星星装饰
      g.lineStyle(0);
      g.beginFill(0xFFD700, 0.6);
      drawStar(g, cx + 14, cy - s * 0.65, 4, 7, 5);
      g.endFill();
      break;
    }
    case 'jail': {
      // 🚔 可爱牢房：圆角栅栏 + 锁
      g.lineStyle(2.5, 0xC4B5A5, 0.85);
      g.drawRoundedRect(cx - 14, cy - 16, 12, 32, 5);
      g.drawRoundedRect(cx + 2, cy - 16, 12, 32, 5);
      // 横向栏杆
      g.moveTo(cx - 14, cy - 8);
      g.lineTo(cx - 2, cy - 8);
      g.moveTo(cx + 2, cy - 8);
      g.lineTo(cx + 14, cy - 8);
      g.moveTo(cx - 14, cy + 4);
      g.lineTo(cx - 2, cy + 4);
      g.moveTo(cx + 2, cy + 4);
      g.lineTo(cx + 14, cy + 4);
      // 大锁
      g.beginFill(0x9B8574, 0.9);
      g.drawRoundedRect(cx - 4, cy + 2, 8, 9, 4);
      g.endFill();
      g.beginFill(0xFFD700);
      g.drawCircle(cx, cy - 2, 2.5);
      g.endFill();
      break;
    }
    case 'chance': {
      // 🎁 可爱宝箱/礼物盒（替代问号）
      g.lineStyle(0);
      // 箱体
      g.beginFill(0xA29BFE, 0.85);
      g.drawRoundedRect(cx - 14, cy - 2, 28, 18, 6);
      g.endFill();
      // 箱盖
      g.beginFill(0x8B7FE8, 0.9);
      g.drawRoundedRect(cx - 15, cy - 14, 30, 16, 8);
      g.endFill();
      // 盖子高光
      g.beginFill(0xFFFFFF, 0.25);
      g.drawRoundedRect(cx - 11, cy - 16, 22, 6, 3);
      g.endFill();
      // 锁扣
      g.beginFill(0xFFD700);
      g.drawCircle(cx, cy - 3, 4);
      g.endFill();
      // 问号标记
      g.beginFill(0xF4A950);
      g.drawRoundedRect(cx - 8, cy + 3, 16, 6, 3);
      g.endFill();
      // 小星星
      g.beginFill(0xFFD700, 0.7);
      drawStar(g, cx + 13, cy - 15, 3, 5, 5);
      g.endFill();
      break;
    }
    case 'event': {
      // ⚡ 可爱气泡对话 + 感叹号
      // 气泡背景
      g.beginFill(0xFFFFFF, 0.85);
      g.drawRoundedRect(cx - 16, cy - 18, 32, 28, 14);
      g.endFill();
      // 气泡尾巴
      g.beginFill(0xFFFFFF, 0.85);
      g.moveTo(cx - 3, cy + 12);
      g.lineTo(cx - 8, cy + 22);
      g.lineTo(cx + 1, cy + 10);
      g.closePath();
      g.endFill();
      // 感叹号
      g.beginFill(0xFF8C69, 0.95);
      g.drawRoundedRect(cx - 3.5, cy - 14, 7, 18, 3.5);
      g.endFill();
      g.beginFill(0xFF8C69, 0.95);
      g.drawCircle(cx, cy + 9, 3.5);
      g.endFill();
      // 小火花
      g.beginFill(0xF4A950, 0.7);
      drawStar(g, cx + 11, cy - 15, 3, 5, 4);
      g.endFill();
      break;
    }
    case 'tax': {
      // 💰 可爱钱袋
      // 袋身
      g.beginFill(0xF4A950, 0.9);
      g.drawRoundedRect(cx - 12, cy - 4, 24, 22, 10);
      g.endFill();
      // 袋口束紧
      g.beginFill(0xE8913A, 0.95);
      g.drawRoundedRect(cx - 10, cy - 10, 20, 10, 5);
      g.endFill();
      // 束绳结
      g.beginFill(0xD4782A);
      g.drawCircle(cx, cy - 12, 3);
      g.endFill();
      // $ 符号
      g.beginFill(0xFFFFFF, 0.9);
      g.drawRoundedRect(cx - 4, cy - 1, 8, 3, 1.5);
      g.drawRoundedRect(cx - 4, cy + 7, 8, 3, 1.5);
      g.drawRoundedRect(cx - 1.5, cy - 2, 3, 14, 1.5);
      g.endFill();
      // 两侧绳头
      g.lineStyle(2, 0xD4782A, 0.7);
      g.moveTo(cx - 6, cy - 12);
      g.arc(cx - 8, cy - 4, 6, Math.PI * 0.8, Math.PI * 1.6, false);
      g.moveTo(cx + 6, cy - 12);
      g.arc(cx + 8, cy - 4, 6, Math.PI * 1.6, Math.PI * 0.8, true);
      break;
    }
    case 'auction': {
      // 🔨 可爱拍卖槌
      // 底座块
      g.beginFill(0x9B8574, 0.85);
      g.drawRoundedRect(cx - 14, cy + 10, 28, 14, 4);
      g.endFill();
      // 槌柄
      g.beginFill(0x8D6E63, 0.9);
      g.drawRoundedRect(cx - 3, cy - s * 0.4, 6, s * 0.75, 3);
      g.endFill();
      // 槌头
      g.beginFill(0x5C4033, 0.9);
      g.drawRoundedRect(cx - 10, cy - s * 0.55, 20, 11, 5);
      g.endFill();
      // 槌头高光
      g.beginFill(0xFFFFFF, 0.2);
      g.drawRoundedRect(cx - 7, cy - s * 0.55, 14, 3, 2);
      g.endFill();
      // 音效线
      g.lineStyle(1.5, 0xF4A950, 0.5);
      g.moveTo(cx + 12, cy - s * 0.65);
      g.quadraticCurveTo(cx + 20, cy - s * 0.85, cx + 16, cy - s * 1.05);
      g.moveTo(cx + 16, cy - s * 0.5);
      g.quadraticCurveTo(cx + 25, cy - s * 0.6, cx + 22, cy - s * 0.85);
      break;
    }
    case 'reverseShop': {
      // 🔄 可爱反转卡牌
      g.lineStyle(0);
      // 左卡
      g.beginFill(0x5CBDB8, 0.9);
      g.drawRoundedRect(cx - 16, cy - 14, 20, 28, 5);
      g.endFill();
      // 右卡
      g.beginFill(0xFF8C69, 0.9);
      g.drawRoundedRect(cx, cy - 10, 20, 28, 5);
      g.endFill();
      // 左卡高光
      g.beginFill(0xFFFFFF, 0.3);
      g.drawRoundedRect(cx - 12, cy - 10, 12, 3, 1.5);
      g.endFill();
      // 右卡高光
      g.beginFill(0xFFFFFF, 0.3);
      g.drawRoundedRect(cx + 4, cy - 6, 12, 3, 1.5);
      g.endFill();
      // 中心反转符号
      g.lineStyle(2.5, 0xFFFFFF, 0.85);
      g.moveTo(cx - 2, cy - 2);
      g.arc(cx - 2, cy - 2, 8, -Math.PI * 0.5, Math.PI * 0.7, false);
      g.moveTo(cx + 6, cy - 4);
      g.lineTo(cx + 2, cy - 8);
      g.moveTo(cx + 6, cy - 4);
      g.lineTo(cx + 10, cy - 6);
      // 小箭头尾
      g.moveTo(cx - 4, cy + 4);
      g.arc(cx - 2, cy - 2, 7, Math.PI * 0.6, Math.PI * 1.8, false);
      g.moveTo(cx - 10, cy + 2);
      g.lineTo(cx - 6, cy + 6);
      g.moveTo(cx - 10, cy + 2);
      g.lineTo(cx - 12, cy - 2);
      break;
    }
    case 'casino': {
      // 🎰 可爱老虎机 + 骰子
      g.lineStyle(0);
      // 机身
      g.beginFill(0xFF8C69, 0.9);
      g.drawRoundedRect(cx - 14, cy - 8, 28, 24, 7);
      g.endFill();
      // 屏幕
      g.beginFill(0xFFFFFF, 0.95);
      g.drawRoundedRect(cx - 10, cy - 5, 20, 16, 4);
      g.endFill();
      // 屏幕边框
      g.lineStyle(1.5, 0xE07050, 0.6);
      g.drawRoundedRect(cx - 10, cy - 5, 20, 16, 4);
      // 转轮符号 — 7 / 🍒 / ⭐
      g.lineStyle(0);
      g.beginFill(0x7EC8A4);
      g.drawCircle(cx - 4, cy + 1, 3.5);
      g.endFill();
      g.beginFill(0xFF6B6B);
      g.drawCircle(cx + 1, cy + 3, 3);
      g.endFill();
      g.beginFill(0xF4A950);
      drawStar(g, cx + 5, cy + 1, 3, 5, 4);
      g.endFill();
      // 顶部装饰
      g.beginFill(0xFFD700, 0.7);
      g.drawRoundedRect(cx - 4, cy - 12, 8, 5, 3);
      g.endFill();
      // 骰子小装饰
      g.beginFill(0xFFFFFF, 0.9);
      g.drawRoundedRect(cx + 10, cy - 14, 8, 8, 2);
      g.endFill();
      g.beginFill(0x5C4033);
      g.drawCircle(cx + 14, cy - 11, 1.2);
      g.endFill();
      break;
    }
    case 'property':
    default: {
      if (street) {
        const sc = COLORS.streetAccent[street] || 0xA29BFE;
        const sd = COLORS.streetBg[street] || 0xD4CCF8;
        // 🏠 可爱小房子
        g.lineStyle(0);
        // 房子主体
        g.beginFill(sd, 0.95);
        g.drawRoundedRect(cx - 12, cy - 4, 24, 20, 5);
        g.endFill();
        // 屋顶
        g.beginFill(sc, 0.9);
        g.moveTo(cx - 16, cy - 4);
        g.lineTo(cx, cy - 21);
        g.lineTo(cx + 16, cy - 4);
        g.closePath();
        g.endFill();
        // 屋顶高光
        g.beginFill(0xFFFFFF, 0.2);
        g.moveTo(cx - 8, cy - 7);
        g.lineTo(cx, cy - 17);
        g.lineTo(cx, cy - 7);
        g.closePath();
        g.endFill();
        // 门
        g.beginFill(0xFFFFFF, 0.55);
        g.drawRoundedRect(cx - 3.5, cy + 4, 7, 11, 3.5);
        g.endFill();
        // 门把手
        g.beginFill(sc, 0.8);
        g.drawCircle(cx + 2, cy + 10, 1.5);
        g.endFill();
        // 窗户
        g.beginFill(0xFFFFFF, 0.45);
        g.drawRoundedRect(cx - 10, cy - 1, 5, 5, 1.5);
        g.drawRoundedRect(cx + 5, cy - 1, 5, 5, 1.5);
        g.endFill();
      } else {
        // 默认灰色房子
        g.beginFill(0xD4D0C8, 0.7);
        g.drawRoundedRect(cx - 12, cy - 4, 24, 20, 5);
        g.endFill();
        g.beginFill(0xB4B0A8);
        g.moveTo(cx - 16, cy - 4);
        g.lineTo(cx, cy - 21);
        g.lineTo(cx + 16, cy - 4);
        g.closePath();
        g.endFill();
        g.beginFill(0xFFFFFF, 0.4);
        g.drawRoundedRect(cx - 3.5, cy + 4, 7, 11, 3.5);
        g.endFill();
      }
      break;
    }
  }
}

// ============ 动画辅助类型 ============
interface AnimTask {
  update: (now: number) => boolean; // 返回 true 表示完成
}

// ============ BoardRenderer ============
export class BoardRenderer {
  app: PIXI.Application;
  boardContainer: PIXI.Container;
  cellSprites: Map<number, PIXI.Container> = new Map();
  playerSprites: Map<string, PIXI.Container> = new Map();
  buildingSprites: Map<string, PIXI.Container> = new Map();
  ownershipMarkers: Map<number, PIXI.Graphics> = new Map();
  playerPositionHighlights: Map<number, PIXI.Container> = new Map(); // 玩家站立格子高亮
  cellSize = 155;
  currentWidth = 800;
  currentHeight = 600;
  baseScale = 1; // 基础缩放（根据屏幕计算）
  baseX = 0;
  baseY = 0;

  private _isDestroyed = false;
  private _onHover: ((cellId: number, screenX: number, screenY: number) => void) | null = null;
  private _onHoverOut: (() => void) | null = null;
  private _onClick: ((cellId: number) => void) | null = null;
  private _dragMoved = false;
  private _animTasks: AnimTask[] = [];
  private _animRaf = 0;
  private _renderRaf = 0;       // 渲染循环的 raf id（独立于动画循环）
  private _currentPath: number[] = [];
  private _currentPathIndex = 0;
  private _onVisibilityChange: (() => void) | null = null;

  // 行走动画精灵表
  private _walkTextures: Map<string, PIXI.Texture[]> = new Map();  // playerId -> 4帧纹理
  private _walkSprites: Map<string, PIXI.Sprite> = new Map();      // playerId -> 当前行走精灵
  private _walkLoading: Set<string> = new Set();                   // 正在加载中的 playerId

  // tile 图标纹理缓存 (type → PIXI.Texture)
  private _tileTextures: Map<string, PIXI.Texture> = new Map();

  // 建筑等级纹理缓存 (buildingLevel → PIXI.Texture): 1=房子, 2=别墅, 3=酒店
  private _buildingTextures: Map<number, PIXI.Texture> = new Map();

  // CellType → tile 文件名映射
  private static readonly TILE_MAP: Record<string, string> = {
    start:       'start',
    jail:        'jail',
    chance:      'fate',
    event:       'event',
    tax:         'tax',
    auction:     'auction',
    reverseShop: 'reverse_shop',
    casino:      'casino',
    property:    'property',
  };

  constructor(canvas: HTMLCanvasElement, width: number, height: number) {
    this.currentWidth = width;
    this.currentHeight = height;

    // 移动端用较低分辨率 + 关闭抗锯齿以降低 GPU 负载（防发热）
    const isMobile = window.innerWidth < 768;
    this.app = new PIXI.Application({
      view: canvas,
      width,
      height,
      backgroundColor: 0xFFF5EE,
      antialias: !isMobile, // 桌面端开抗锯齿，移动端关闭（GPU 开销大）
      resolution: isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      autoStart: false, // 不自动启动 ticker，改为手动控制
    });

    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';

    this.boardContainer = new PIXI.Container();
    this.app.stage.addChild(this.boardContainer);
    this.centerBoard(width, height);

    // 页面隐藏时暂停渲染（切后台 / 锁屏时省电）
    this._onVisibilityChange = () => {
      if (document.hidden) {
        this._stopRenderLoop();
      } else if (this._animTasks.length > 0) {
        this._startRenderLoop();
      } else {
        // 恢复可见但没有动画 → 只渲染一帧让画面更新
        this.app.renderer.render(this.app.stage);
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    this._startAnimLoop();
    this._enableZoom();
  }

  /**
   * 加载玩家行走精灵表 (walk.png = 480x120, 4 frames x 120x120)
   * 异步加载并缓存为 PIXI.Texture[] 供 _stepNext 使用
   */
  async loadWalkTextures(playerId: string, walkUrl: string): Promise<void> {
    if (this._walkTextures.has(playerId) || this._walkLoading.has(playerId)) return;
    this._walkLoading.add(playerId);
    try {
      const texture = await PIXI.Assets.load<PIXI.Texture>(walkUrl);
      const bt = texture.baseTexture;
      const fw = bt.width / 4;   // 120
      const fh = bt.height;       // 120
      const frames: PIXI.Texture[] = [];
      for (let i = 0; i < 4; i++) {
        frames.push(new PIXI.Texture(bt, new PIXI.Rectangle(i * fw, 0, fw, fh)));
      }
      this._walkTextures.set(playerId, frames);
    } catch (e) {
      // 加载失败静默处理，不影响游戏
      console.warn(`[BoardRenderer] Failed to load walk texture for ${playerId}:`, e);
    } finally {
      this._walkLoading.delete(playerId);
    }
  }

  /**
   * 预加载所有 tile 图标纹理（棋盘渲染前调用）
   * 加载完成后调用 onReady 回调触发棋盘渲染
   */
  async preloadTileTextures(onReady?: () => void): Promise<void> {
    console.log('[BoardRenderer] preloadTileTextures: starting tile texture preload...');
    try {
      const tileTypes = Object.keys(BoardRenderer.TILE_MAP);
      const results = await Promise.allSettled(
        tileTypes.map(async (type) => {
          const fileName = BoardRenderer.TILE_MAP[type];
          const url = ASSETS.tile(fileName);
          try {
            const texture = await PIXI.Assets.load<PIXI.Texture>(url);
            if (texture && texture.valid) {
              this._tileTextures.set(type, texture);
              console.log(`[BoardRenderer] tile texture loaded: ${type} (${url})`);
            }
          } catch (e) {
            console.warn(`[BoardRenderer] tile texture load failed: ${url}`, e);
          }
        })
      );
      const loadedCount = results.filter(r => r.status === 'fulfilled').length;
      console.log(`[BoardRenderer] preloadTileTextures: ${loadedCount}/${tileTypes.length} textures loaded`);
    } catch (e) {
      console.warn('[BoardRenderer] preloadTileTextures error, falling back to vector icons:', e);
    }

    // 预加载建筑等级纹理 (tile_house.png → level 1, tile_villa.png → level 2, tile_hotel.png → level 3)
    try {
      const buildingMap: Record<number, string> = { 1: 'house', 2: 'villa', 3: 'hotel' };
      await Promise.allSettled(
        Object.entries(buildingMap).map(async ([key, fileName]) => {
          const level = Number(key);
          const url = ASSETS.tile(fileName);
          try {
            const texture = await PIXI.Assets.load<PIXI.Texture>(url);
            if (texture && texture.valid) {
              this._buildingTextures.set(level, texture);
              console.log(`[BoardRenderer] building texture loaded: level=${level} (${url})`);
            }
          } catch (e) {
            console.warn(`[BoardRenderer] building texture load failed: ${url}`, e);
          }
        })
      );
    } catch (e) {
      console.warn('[BoardRenderer] building texture preload error:', e);
    } finally {
      if (onReady) onReady();
    }
  }

  setHoverCallback(
    onHover: (cellId: number, screenX: number, screenY: number) => void,
    onHoverOut: () => void,
  ) {
    this._onHover = onHover;
    this._onHoverOut = onHoverOut;
  }

  /** 设置点击回调（仅棋盘格，区分拖拽与点击） */
  setClickCallback(onClick: (cellId: number) => void) {
    this._onClick = onClick;
  }

  private _selectedCell: number | null = null;
  private _lastClickTime = 0;
  private _lastClickCell = -1;

  /** 设置高亮选中的格子 */
  setSelectedCell(cellId: number | null) {
    this._selectedCell = cellId;
    this._redrawSelection();
    this._requestRender();
  }

  private _redrawSelection() {
    // 每格渲染时会检查 _selectedCell 并添加高亮
    // 这里触发一次完整重绘
  }

  /** 闪烁高亮指定格子（土地丢失/降级提示后使用） */
  flashCell(cellId: number) {
    if (this._isDestroyed) return;
    const pos = this.getCellPos(cellId);
    const cs = this.cellSize;

    const glow = new PIXI.Graphics();
    glow.name = '__flash_cell';

    // 绘制三层脉冲光环
    const drawGlow = (alpha: number) => {
      glow.clear();
      // 外层：粗亮光
      glow.lineStyle(3, 0xFF6B35, alpha * 0.8);
      glow.drawRoundedRect(pos.x - 4, pos.y - 4, cs + 8, cs + 8, 12);
      // 中层
      glow.lineStyle(2, 0xFFD700, alpha * 0.6);
      glow.drawRoundedRect(pos.x - 2, pos.y - 2, cs + 4, cs + 4, 10);
      // 内层填充
      glow.beginFill(0xFF4444, alpha * 0.15);
      glow.drawRoundedRect(pos.x + 2, pos.y + 2, cs - 4, cs - 4, 6);
      glow.endFill();
    };

    drawGlow(0);
    this.boardContainer.addChild(glow);

    // 使用动画系统实现 3 次脉冲
    const totalPulses = 3;
    const pulseDuration = 400; // 每次脉冲 400ms
    this._addAnim(pulseDuration * totalPulses, (t) => {
      const pulseT = (t * totalPulses) % 1; // 0→1 循环
      const alpha = Math.sin(pulseT * Math.PI); // 正弦脉冲
      drawGlow(alpha);
      this._requestRender();
    }, () => {
      // 动画结束 → 最后一帧残留光晕然后渐隐
      const fadeStart = performance.now();
      const fadeDuration = 300;
      this._addAnim(fadeDuration, (ft) => {
        const fa = 1 - ft;
        drawGlow(fa * 0.6);
        this._requestRender();
      }, () => {
        // 彻底移除
        if (glow.parent) {
          glow.parent.removeChild(glow);
          this._requestRender();
        }
      });
    });
  }

  resize(width: number, height: number) {
    if (this._isDestroyed) return;
    this.currentWidth = width;
    this.currentHeight = height;
    this.app.renderer.resize(width, height);
    this.centerBoard(width, height);
    const canvas = this.app.renderer.view as HTMLCanvasElement;
    if (canvas) {
      canvas.style.width = '100%';
      canvas.style.height = '100%';
    }
    // autoStart: false 模式下 resize 后需手动触发渲染，否则画面保持 resize 前的内容或空白
    this._requestRender();
  }

  centerBoard(width: number, height: number) {
    const boardWidth = 11 * this.cellSize;
    // 动态底部留白：屏幕越小预留越少
    // hPad 也为窄屏（<400px，竖屏手机常见）减少侧边留白
    const vPad = height < 300 ? 4 : (height < 400 ? 20 : (height < 500 ? 48 : (height < 700 ? 80 : 160)));
    const hPad = width < 350 ? 2 : (width < 400 ? 8 : (width < 500 ? 20 : 40));
    const scale = Math.min(
      (width - hPad) / boardWidth,
      (height - vPad) / boardWidth,
      1,
    );
    this.baseScale = scale;
    this.baseX = (width - boardWidth * scale) / 2;
    this.baseY = Math.max(5, (height - vPad - boardWidth * scale) / 2);
    this.boardContainer.scale.set(scale);
    this.boardContainer.x = this.baseX;
    this.boardContainer.y = this.baseY;
  }

  // ============ 渲染 & 动画循环 ============
  // 策略：动画任务存在时保持渲染循环；任务清空后渲染一帧收尾即停止。
  // 避免空转 60fps 导致手机发热。

  private _startRenderLoop() {
    if (this._isDestroyed || this._renderRaf !== 0) return;
    const loop = () => {
      if (this._isDestroyed) return;
      if (this._animTasks.length === 0) {
        // 没有动画了 → 渲染最后一帧收尾图片，然后停止渲染循环
        this.app.renderer.render(this.app.stage);
        this._renderRaf = 0;
        return;
      }
      this.app.renderer.render(this.app.stage);
      this._renderRaf = requestAnimationFrame(loop);
    };
    this._renderRaf = requestAnimationFrame(loop);
  }

  private _stopRenderLoop() {
    if (this._renderRaf !== 0) {
      cancelAnimationFrame(this._renderRaf);
      this._renderRaf = 0;
    }
  }

  private _startAnimLoop() {
    const loop = (now: number) => {
      if (this._isDestroyed) return;
      // 更新动画状态（不负责渲染）
      this._animTasks = this._animTasks.filter(t => !t.update(now));

      if (this._animTasks.length === 0) {
        // 动画结束 → 渲染一帧收尾，然后停止渲染
        this.app.renderer.render(this.app.stage);
        this._stopRenderLoop();
        this._animRaf = 0;
        return; // 停止动画循环
      }

      this._animRaf = requestAnimationFrame(loop);
    };
    this._animRaf = requestAnimationFrame(loop);
    // 动画开始时启动渲染循环
    this._startRenderLoop();
  }

  /** 在交互（拖拽/缩放）时触发单帧渲染 */
  private _requestRender() {
    if (this._isDestroyed) return;
    if (document.hidden) return; // 页面隐藏时不渲染，省电
    this.app.renderer.render(this.app.stage);
  }

  private _addAnim(duration: number, onUpdate: (t: number) => void, onComplete?: () => void): void {
    const wasEmpty = this._animTasks.length === 0;
    const startTime = performance.now();
    let done = false;
    this._animTasks.push({
      update: (now: number) => {
        if (done) return true;
        const t = Math.min((now - startTime) / duration, 1);
        const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic
        onUpdate(ease);
        if (t >= 1) {
          done = true;
          onComplete?.();
          return true;
        }
        return false;
      },
    });

    // 如果之前没有任务，需要重新启动动画+渲染循环
    if (wasEmpty) {
      this._animRaf = 0;
      this._startAnimLoop();
    }
  }

  // ============ 鼠标缩放 + 拖拽平移 ============
  private _isDragging = false;
  private _dragStartX = 0;
  private _dragStartY = 0;
  private _dragStartBoardX = 0;
  private _dragStartBoardY = 0;
  // 双指捏合缩放
  private _pinching = false;
  private _pinchPointers = new Map<number, { x: number; y: number }>();
  private _pinchInitialDist = 0;
  private _pinchInitialScale = 1;
  private _pinchMidX = 0;
  private _pinchMidY = 0;
  private _pinchBoardX = 0;
  private _pinchBoardY = 0;
  // 移动端长按检测
  private _longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private _longPressTriggered = false;

  private _enableZoom() {
    const stage = this.app.stage;
    stage.eventMode = 'static';
    stage.hitArea = new PIXI.Rectangle(0, 0, 10000, 10000);

    // --- 鼠标滚轮缩放（向鼠标位置） ---
    stage.on('wheel', (e: PIXI.FederatedWheelEvent) => {
      if (this._isDestroyed) return;
      e.preventDefault();
      const currentScale = this.boardContainer.scale.x;
      const delta = e.deltaY > 0 ? -0.08 : 0.08;
      const newScale = Math.max(0.4, Math.min(2.5, currentScale + delta));

      // Zoom toward mouse position
      const mouseX = e.globalX;
      const mouseY = e.globalY;
      const worldX = (mouseX - this.boardContainer.x) / currentScale;
      const worldY = (mouseY - this.boardContainer.y) / currentScale;

      this.boardContainer.scale.set(newScale);
      this.boardContainer.x = mouseX - worldX * newScale;
      this.boardContainer.y = mouseY - worldY * newScale;
      this._requestRender();
    });
    stage.cursor = 'grab';

    stage.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
      if (this._isDestroyed) return;
      if (e.button !== 0) return;

      // 记录 pointer（用于双指捏合）
      this._pinchPointers.set(e.pointerId, { x: e.globalX, y: e.globalY });

      // 第二个手指按下 → 进入双指捏合模式
      if (this._pinchPointers.size >= 2) {
        this._isDragging = false;
        this._pinching = true;
        // 取消长按计时器
        if (this._longPressTimer) {
          clearTimeout(this._longPressTimer);
          this._longPressTimer = null;
        }
        const pts = Array.from(this._pinchPointers.values());
        this._pinchInitialDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        this._pinchInitialScale = this.boardContainer.scale.x;
        this._pinchMidX = (pts[0].x + pts[1].x) / 2;
        this._pinchMidY = (pts[0].y + pts[1].y) / 2;
        this._pinchBoardX = this.boardContainer.x;
        this._pinchBoardY = this.boardContainer.y;
        return;
      }

      // 单指 → 拖拽/长按
      this._isDragging = true;
      this._pinching = false;
      this._dragMoved = false;
      this._dragStartX = e.globalX;
      this._dragStartY = e.globalY;
      this._dragStartBoardX = this.boardContainer.x;
      this._dragStartBoardY = this.boardContainer.y;
      stage.cursor = 'grabbing';

      // 移动端：启动长按计时器（600ms），到期触发格子详情
      const isMobile = window.innerWidth < 768;
      if (isMobile && this._onClick) {
        this._longPressTriggered = false;
        const cellId = this._screenToCell(e.globalX, e.globalY);
        this._longPressTimer = setTimeout(() => {
          if (this._isDestroyed) return;
          if (!this._dragMoved && cellId >= 0 && !this._longPressTriggered) {
            this._longPressTriggered = true;
            this._onClick!(cellId);
          }
          this._longPressTimer = null;
        }, 600);
      }
    });

    stage.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
      if (this._isDestroyed) return;

      // 更新 pointer 位置
      this._pinchPointers.set(e.pointerId, { x: e.globalX, y: e.globalY });

      // 双指捏合缩放
      if (this._pinching && this._pinchPointers.size >= 2) {
        const pts = Array.from(this._pinchPointers.values());
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (this._pinchInitialDist < 1) return;
        const ratio = dist / this._pinchInitialDist;
        const newScale = Math.max(0.4, Math.min(2.5, this._pinchInitialScale * ratio));

        // 以双指中点为锚点缩放
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;

        this.boardContainer.scale.set(newScale);
        this.boardContainer.x = midX - (this._pinchMidX - this._pinchBoardX) * (newScale / this._pinchInitialScale);
        this.boardContainer.y = midY - (this._pinchMidY - this._pinchBoardY) * (newScale / this._pinchInitialScale);
        this._requestRender();
        return;
      }

      if (!this._isDragging) return;
      const dx = e.globalX - this._dragStartX;
      const dy = e.globalY - this._dragStartY;
      const dragThreshold = window.innerWidth < 768 ? 10 : 3;
      if (Math.abs(dx) > dragThreshold || Math.abs(dy) > dragThreshold) {
        this._dragMoved = true;
        // 移动端手指移动超过阈值 → 取消长按计时器
        if (this._longPressTimer) {
          clearTimeout(this._longPressTimer);
          this._longPressTimer = null;
        }
      }
      this.boardContainer.x = this._dragStartBoardX + dx;
      this.boardContainer.y = this._dragStartBoardY + dy;
      this._requestRender();
    });

    stage.on('pointerup', (e: PIXI.FederatedPointerEvent) => {
      if (this._isDestroyed) return;

      // 清理 pointer 记录
      this._pinchPointers.delete(e.pointerId);
      if (this._pinchPointers.size < 2) {
        this._pinching = false;
      }

      this._isDragging = false;
      stage.cursor = 'grab';

      // 移动端：取消长按计时器，点击由计时器触发
      if (this._longPressTimer) {
        clearTimeout(this._longPressTimer);
        this._longPressTimer = null;
      }

      const isMobile = window.innerWidth < 768;
      if (isMobile) return; // 移动端不在此触发点击

      // PC 端：没有拖动 → 这是单击（防抖：300ms 内同格双击忽略）
      if (!this._dragMoved && this._onClick) {
        const cellId = this._screenToCell(e.globalX, e.globalY);
        if (cellId >= 0) {
          const now = Date.now();
          if (now - this._lastClickTime < 300 && cellId === this._lastClickCell) {
            return;
          }
          this._lastClickTime = now;
          this._lastClickCell = cellId;
          this._onClick(cellId);
        }
      }
    });

    stage.on('pointerupoutside', (e: PIXI.FederatedPointerEvent) => {
      if (this._isDestroyed) return;

      // 清理 pointer 记录
      this._pinchPointers.delete(e.pointerId);
      if (this._pinchPointers.size < 2) {
        this._pinching = false;
      }

      this._isDragging = false;
      stage.cursor = 'grab';

      // 移动端：取消长按计时器
      if (this._longPressTimer) {
        clearTimeout(this._longPressTimer);
        this._longPressTimer = null;
      }

      const isMobile = window.innerWidth < 768;
      if (isMobile) return; // 移动端不在此触发点击

      // PC 端：如果位移未超过拖拽阈值且有按下坐标，视为点击
      if (!this._dragMoved && this._onClick) {
        const cellId = this._screenToCell(this._dragStartX, this._dragStartY);
        if (cellId >= 0) {
          const now = Date.now();
          if (now - this._lastClickTime < 300 && cellId === this._lastClickCell) {
            return;
          }
          this._lastClickTime = now;
          this._lastClickCell = cellId;
          this._onClick(cellId);
        }
      }
    });
  }

  // ============ 相机操作 ============
  /** 聚焦到指定格子的世界坐标位置 */
  focusOn(worldX: number, worldY: number, scale: number, duration: number, onComplete?: () => void): void {
    const currentScale = this.boardContainer.scale.x;
    const currentX = this.boardContainer.x;
    const currentY = this.boardContainer.y;

    const targetScale = scale;
    const targetX = this.currentWidth / 2 - worldX * targetScale;
    const targetY = this.currentHeight / 2 - worldY * targetScale;

    this._addAnim(duration,
      (t) => {
        const s = currentScale + (targetScale - currentScale) * t;
        this.boardContainer.scale.set(s);
        this.boardContainer.x = currentX + (targetX - currentX) * t;
        this.boardContainer.y = currentY + (targetY - currentY) * t;
      },
      onComplete
    );
  }

  /** 聚焦到格子 */
  focusOnCell(cellId: number, scale: number, duration: number, onComplete?: () => void): void {
    const pos = this.getCellPos(cellId);
    const innerSize = this.cellSize - 6;
    const worldX = pos.x + innerSize / 2;
    const worldY = pos.y + innerSize / 2;
    this.focusOn(worldX, worldY, scale, duration, onComplete);
  }

  /** 重置视角 */
  resetView(duration: number, onComplete?: () => void): void {
    const currentScale = this.boardContainer.scale.x;
    const currentX = this.boardContainer.x;
    const currentY = this.boardContainer.y;

    this._addAnim(duration,
      (t) => {
        this.boardContainer.scale.set(currentScale + (this.baseScale - currentScale) * t);
        this.boardContainer.x = currentX + (this.baseX - currentX) * t;
        this.boardContainer.y = currentY + (this.baseY - currentY) * t;
      },
      onComplete
    );
  }

  // ============ 渲染棋盘 ============
  renderBoard(cells: BoardCell[]) {
    if (this._isDestroyed) return;
    this.cellSprites.clear();
    this.ownershipMarkers.clear();
    this.boardContainer.removeChildren();

    const cs = this.cellSize;
    const gridSize = 11 * cs;

    // 棋盘背景（柔色 + 圆角）
    const bg = new PIXI.Graphics();
    // 外层柔和阴影
    bg.beginFill(0xE8D5C0, 0.4);
    bg.drawRoundedRect(3, 3, gridSize, gridSize, 22);
    bg.endFill();
    // 棋盘主体
    bg.beginFill(0xFFECD2, 1);
    bg.drawRoundedRect(0, 0, gridSize, gridSize, 20);
    bg.endFill();
    // 内边框装饰
    bg.lineStyle(2, 0xF0DCC0, 0.6);
    bg.drawRoundedRect(6, 6, gridSize - 12, gridSize - 12, 16);
    this.boardContainer.addChild(bg);

    // 渲染所有格子（单格异常不影响其他格）
    cells.forEach((cell) => {
      try {
        const container = this.renderCell(cell);
        this.cellSprites.set(cell.id, container);
        this.boardContainer.addChild(container);
      } catch (e) {
        console.error(`[BoardRenderer] renderCell failed for cell ${cell.id} (${cell.name}):`, e);
      }
    });

    // 选中格子高亮
    if (this._selectedCell !== null && this._selectedCell >= 0) {
      const selPos = this.getCellPos(this._selectedCell);
      const selGlow = new PIXI.Graphics();
      selGlow.lineStyle(4, 0xFFD700, 0.9);
      selGlow.drawRoundedRect(selPos.x - 2, selPos.y - 2, cs + 4, cs + 4, 10);
      selGlow.lineStyle(2, 0xFFD700, 0.4);
      selGlow.drawRoundedRect(selPos.x - 6, selPos.y - 6, cs + 12, cs + 12, 14);
      selGlow.name = '__selection';
      this.boardContainer.addChild(selGlow);
    }

    // 中心标题
    const titleBg = new PIXI.Graphics();
    titleBg.beginFill(0xFFFFFF, 0.85);
    titleBg.drawRoundedRect(gridSize / 2 - 220, gridSize / 2 - 50, 440, 100, 22);
    titleBg.endFill();
    titleBg.lineStyle(2, 0xFF8C69, 0.25);
    titleBg.drawRoundedRect(gridSize / 2 - 220, gridSize / 2 - 50, 440, 100, 22);
    this.boardContainer.addChild(titleBg);

    const titleText = new PIXI.Text('翻转大富翁', {
      fontSize: 42,
      fill: 0xE8913A,
      fontWeight: 'bold',
      fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
      align: 'center',
    });
    titleText.anchor.set(0.5);
    titleText.x = gridSize / 2;
    titleText.y = gridSize / 2 - 10;
    this.boardContainer.addChild(titleText);

    const subtitleText = new PIXI.Text('🎲 每一轮都可能天翻地覆', {
      fontSize: 16,
      fill: 0x9B8574,
      fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
    });
    subtitleText.anchor.set(0.5);
    subtitleText.x = gridSize / 2;
    subtitleText.y = gridSize / 2 + 28;
    this.boardContainer.addChild(subtitleText);

    // ============ 街区标注 ============
    this._renderDistrictLabels(cs, gridSize);

    // 确保初次渲染后有一帧画面（autoStart: false 需要手动触发）
    this._requestRender();
  }

  /**
   * 在地图中央绘制四个街区的名称标注
   * 带有彩色背景框和指向对应格子的括号线
   */
  private _renderDistrictLabels(cs: number, gridSize: number) {
    const districts = [
      { name: '霓虹城区', color: COLORS.streetAccent['霓虹城区'], bg: COLORS.streetBg['霓虹城区'],
        x: gridSize / 2, y: cs * 1.9, side: 'top' as const,
        rangeLabel: '单元格 19-27', cells: [20, 22, 23, 25, 26] },
      { name: '蒸汽都市', color: COLORS.streetAccent['蒸汽都市'], bg: COLORS.streetBg['蒸汽都市'],
        x: cs * 2.0, y: gridSize / 2, side: 'left' as const,
        rangeLabel: '单元格 9-17', cells: [9, 10, 12, 13, 15, 17] },
      { name: '云端天际', color: COLORS.streetAccent['云端天际'], bg: COLORS.streetBg['云端天际'],
        x: gridSize - cs * 2.0, y: gridSize / 2, side: 'right' as const,
        rangeLabel: '单元格 29-35', cells: [29, 30, 32, 34, 35] },
      { name: '像素小镇', color: COLORS.streetAccent['像素小镇'], bg: COLORS.streetBg['像素小镇'],
        x: gridSize / 2, y: gridSize - cs * 1.9, side: 'bottom' as const,
        rangeLabel: '单元格 1-7 / 38-39', cells: [1, 2, 4, 5, 7, 38, 39] },
    ];

    districts.forEach((d) => {
      const box = new PIXI.Graphics();
      const boxW = d.side === 'top' || d.side === 'bottom' ? 170 : 150;
      const boxH = 56;

      // 半透明背景
      box.beginFill(0xFFFFFF, 0.85);
      box.drawRoundedRect(-boxW / 2, -boxH / 2, boxW, boxH, 12);
      box.endFill();
      // 边框
      box.lineStyle(1.5, d.color, 0.5);
      box.drawRoundedRect(-boxW / 2, -boxH / 2, boxW, boxH, 12);
      box.x = d.x;
      box.y = d.y;
      this.boardContainer.addChild(box);

      // 街区名称
      const nameText = new PIXI.Text(d.name, {
        fontSize: 18,
        fill: d.color,
        fontWeight: 'bold',
        fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
      });
      nameText.anchor.set(0.5);
      nameText.x = d.x;
      nameText.y = d.y - 8;
      this.boardContainer.addChild(nameText);

      // 格子范围
      const rangeText = new PIXI.Text(d.rangeLabel, {
        fontSize: 11,
        fill: 0x9B8574,
        fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
      });
      rangeText.anchor.set(0.5);
      rangeText.x = d.x;
      rangeText.y = d.y + 14;
      this.boardContainer.addChild(rangeText);

      // 括号指针线（从标注框指向对应格子区域）
      const lineG = new PIXI.Graphics();
      lineG.lineStyle(1, d.color, 0.3);

      if (d.side === 'top') {
        // 两条线从标注框顶部延伸到上方格子区域
        lineG.moveTo(d.x - 40, d.y - boxH / 2);
        lineG.lineTo(d.x - 40, cs * 0.6);
        lineG.moveTo(d.x + 40, d.y - boxH / 2);
        lineG.lineTo(d.x + 40, cs * 0.6);
      } else if (d.side === 'bottom') {
        lineG.moveTo(d.x - 40, d.y + boxH / 2);
        lineG.lineTo(d.x - 40, gridSize - cs * 0.6);
        lineG.moveTo(d.x + 40, d.y + boxH / 2);
        lineG.lineTo(d.x + 40, gridSize - cs * 0.6);
      } else if (d.side === 'left') {
        lineG.moveTo(d.x - boxW / 2, d.y - 25);
        lineG.lineTo(cs * 0.6, d.y - 25);
        lineG.moveTo(d.x - boxW / 2, d.y + 25);
        lineG.lineTo(cs * 0.6, d.y + 25);
      } else if (d.side === 'right') {
        lineG.moveTo(d.x + boxW / 2, d.y - 25);
        lineG.lineTo(gridSize - cs * 0.6, d.y - 25);
        lineG.moveTo(d.x + boxW / 2, d.y + 25);
        lineG.lineTo(gridSize - cs * 0.6, d.y + 25);
      }

      this.boardContainer.addChild(lineG);
    });
  }

  renderCell(cell: BoardCell): PIXI.Container {
    const container = new PIXI.Container();
    const { x, y } = this.getCellPos(cell.id);
    const pad = 6;
    const inner = this.cellSize - pad * 2;

    // 确定颜色
    let accentColor: number;
    let bgColor: number;
    if (cell.type === 'property' && cell.street) {
      accentColor = COLORS.streetAccent[cell.street] || 0x9B8574;
      bgColor = COLORS.streetBg[cell.street] || 0xFFECD2;
    } else {
      accentColor = COLORS.specialAccent[cell.type] || 0x9B8574;
      bgColor = COLORS.specialBg[cell.type] || 0xFFF5EE;
    }

    // --- 提前返回：有 tile 纹理且非 property → 只显示 3D 图标（无格子背景）---
    const tileTexture = this._tileTextures.get(cell.type);
    const hasTile = tileTexture && tileTexture.valid && !tileTexture.destroyed;
    if (hasTile && cell.type !== 'property') {
      const spriteSize = this.cellSize * 0.72;
      const tileSprite = new PIXI.Sprite(tileTexture);
      tileSprite.width = spriteSize;
      tileSprite.height = spriteSize;
      tileSprite.anchor.set(0.5);
      tileSprite.x = inner / 2;
      tileSprite.y = inner / 2;
      container.addChild(tileSprite);
      container.x = x;
      container.y = y;

      // --- 交互注册（提前返回路径不能跳过交互设置）---
      container.eventMode = 'static';
      container.cursor = 'pointer';
      container.name = `cell_${cell.id}`;

      const onHover = this._onHover;
      const onHoverOut = this._onHoverOut;
      container.on('pointerover', () => {
        container.alpha = 0.88;
        container.scale.set(1.08);
        if (onHover) {
          const boardScale = this.boardContainer.scale.x;
          onHover(
            cell.id,
            this.boardContainer.x + x * boardScale,
            this.boardContainer.y + (y + inner) * boardScale,
          );
        }
      });
      container.on('pointerout', () => {
        container.alpha = 1;
        container.scale.set(1);
        if (onHoverOut) onHoverOut();
      });

      return container;   // 不画 outerBg / panel，直接返回
    }

    // --- 格子外框（白色底色 + 柔和阴影） ---
    const outerBg = new PIXI.Graphics();
    outerBg.beginFill(0xFFFFFF, 0.75);
    outerBg.lineStyle(1, accentColor, 0.18);
    outerBg.drawRoundedRect(0, 0, inner, inner, 8);
    outerBg.endFill();
    container.addChild(outerBg);

    // --- 彩色核心区域（缩小到65%） ---
    const panelW = inner * 0.65;
    const panelH = inner * 0.58;
    const panelX = (inner - panelW) / 2;
    const panelY = pad + 1;

    const panel = new PIXI.Graphics();
    panel.beginFill(bgColor, 0.8);
    panel.lineStyle(2.5, accentColor, 0.45);
    panel.drawRoundedRect(panelX, panelY, panelW, panelH, 10);
    panel.endFill();
    // 顶部柔和高光
    panel.beginFill(0xFFFFFF, 0.3);
    panel.drawRoundedRect(panelX + 4, panelY + 3, panelW - 8, panelH * 0.3, 5);
    panel.endFill();
    container.addChild(panel);

    // --- 大号地形图标 ---
    const iconCx = panelX + panelW / 2;
    const iconCy = panelY + panelH / 2 + 2;
    // tileTexture 已在方法顶部声明（1157行），此处直接使用
    if (tileTexture && tileTexture.valid && !tileTexture.destroyed) {
      try {
        // 3D 卡通 tile 图片 (512x512 → 等比缩放适配面板)
        const iconSize = Math.min(panelW, panelH) * 0.85;
        const tileSprite = new PIXI.Sprite(tileTexture);
        tileSprite.width = iconSize;
        tileSprite.height = iconSize;
        tileSprite.anchor.set(0.5);
        tileSprite.x = iconCx;
        tileSprite.y = iconCy;
        container.addChild(tileSprite);
      } catch (e) {
        // 创建 Sprite 失败 → 降级为矢量
        console.warn('[BoardRenderer] Sprite creation failed for', cell.type, e);
        const iconG = new PIXI.Graphics();
        drawTerrainIcon(iconG, cell.type, cell.street, iconCx, iconCy);
        container.addChild(iconG);
      }
    } else {
      // fallback: 矢量绘制（纹理尚未加载或已失效时使用）
      const iconG = new PIXI.Graphics();
      drawTerrainIcon(iconG, cell.type, cell.street, iconCx, iconCy);
      container.addChild(iconG);
    }

    // --- 名称（大号） ---
    const nameY = panelY + panelH + 4;
    const nameText = new PIXI.Text(cell.name, {
      fontSize: cell.name.length > 4 ? 12 : 14,
      fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
      fill: 0x5C4033,
      align: 'center',
      fontWeight: 'bold',
    });
    nameText.anchor.set(0.5, 0);
    nameText.x = inner / 2;
    nameText.y = nameY;
    container.addChild(nameText);

    // --- 价格 ---
    if (cell.price && cell.type === 'property') {
      const priceText = new PIXI.Text(`¥${cell.price}`, {
        fontSize: 12,
        fill: 0xE8913A,
        align: 'center',
        fontFamily: 'monospace',
        fontWeight: 'bold',
      });
      priceText.anchor.set(0.5, 0);
      priceText.x = inner / 2;
      priceText.y = nameY + 18;
      container.addChild(priceText);
    }

    // --- 特殊说明文字 ---
    if (!cell.price && cell.type !== 'property') {
      let desc = '';
      if (cell.type === 'chance') desc = '抽卡';
      else if (cell.type === 'event') desc = '事件';
      else if (cell.type === 'tax') desc = '缴税';
      else if (cell.type === 'start') desc = '+200金币';
      else if (cell.type === 'jail') desc = '监狱';
      else if (cell.type === 'reverseShop') desc = '反转卡';
      else if (cell.type === 'auction') desc = '拍卖';
      else if (cell.type === 'casino') desc = '赌场';

      const descText = new PIXI.Text(desc, {
        fontSize: 10,
        fill: accentColor,
        fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
        align: 'center',
      });
      descText.anchor.set(0.5, 0);
      descText.x = inner / 2;
      descText.y = nameY + 18;
      container.addChild(descText);
    }

    container.x = x;
    container.y = y;
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.name = `cell_${cell.id}`;

    // 悬停效果
    const onHover = this._onHover;
    const onHoverOut = this._onHoverOut;

    container.on('pointerover', () => {
      container.alpha = 0.85;
      container.scale.set(1.06);
      if (onHover) {
        const globalPos = container.getGlobalPosition();
        const boardScale = this.boardContainer.scale.x;
        onHover(
          cell.id,
          this.boardContainer.x + x * boardScale,
          this.boardContainer.y + (y + inner) * boardScale,
        );
      }
    });
    container.on('pointerout', () => {
      container.alpha = 1;
      container.scale.set(1);
      if (onHoverOut) onHoverOut();
    });

    return container;
  }

  // ============ 渲染玩家棋子（可爱圆脸风格） ============
  renderPlayers(players: Player[]) {
    if (this._isDestroyed) return;

    this.playerSprites.forEach(sprite => {
      sprite.destroy({ children: true });
      if (sprite.parent) sprite.parent.removeChild(sprite);
    });
    this.playerSprites.clear();

    players.forEach((player, index) => {
      if (player.bankrupt) return;
      const parentCell = this.cellSprites.get(player.position);
      if (!parentCell) return;

      const sprite = new PIXI.Container();
      const color = COLORS.playerColors[index % 4];
      const darkColor = COLORS.playerDark[index % 4];
      const inner = this.cellSize - 6;
      const radius = 17;

      // 柔和阴影
      const shadow = new PIXI.Graphics();
      shadow.beginFill(0x000000, 0.12);
      shadow.drawEllipse(0, 3, radius + 1, radius * 0.55);
      shadow.endFill();
      sprite.addChild(shadow);

      // 主体圆形（可爱质感）
      const body = new PIXI.Graphics();
      // 暗色底边
      body.beginFill(darkColor, 0.5);
      body.drawCircle(0, 2, radius);
      body.endFill();
      // 主体色
      body.beginFill(color, 1);
      body.drawCircle(0, -1, radius - 1);
      body.endFill();
      // 顶部高光
      body.beginFill(0xFFFFFF, 0.5);
      body.drawEllipse(-4, -7, 6, 4.5);
      body.endFill();
      // 底部柔光
      body.beginFill(0xFFFFFF, 0.15);
      body.drawEllipse(0, 6, 10, 4);
      body.endFill();
      sprite.addChild(body);

      // 可爱小脸
      const face = new PIXI.Graphics();
      // 左眼
      face.beginFill(0xFFFFFF, 0.95);
      face.drawCircle(-5, -2, 4.5);
      face.endFill();
      face.beginFill(0x5C4033);
      face.drawCircle(-4, -1.5, 2.2);
      face.endFill();
      // 右眼
      face.beginFill(0xFFFFFF, 0.95);
      face.drawCircle(5, -2, 4.5);
      face.endFill();
      face.beginFill(0x5C4033);
      face.drawCircle(6, -1.5, 2.2);
      face.endFill();
      // 微笑
      face.lineStyle(1.5, 0x5C4033, 0.7);
      face.arc(0, 2, 4, Math.PI * 0.15, Math.PI * 0.85, false);
      // 小腮红
      face.beginFill(color, 0.4);
      face.drawCircle(-8, 4, 3);
      face.drawCircle(8, 4, 3);
      face.endFill();
      sprite.addChild(face);

      // 外圈细光环
      const ring = new PIXI.Graphics();
      ring.lineStyle(2, color, 0.35);
      ring.drawCircle(0, 0, radius + 2);
      sprite.addChild(ring);

      // 玩家编号小圆标（右上角）
      const badge = new PIXI.Graphics();
      badge.beginFill(0xFFFFFF, 0.9);
      badge.drawCircle(radius - 2, -radius + 2, 8);
      badge.endFill();
      badge.lineStyle(1.5, color, 0.6);
      badge.drawCircle(radius - 2, -radius + 2, 8);
      sprite.addChild(badge);

      const numText = new PIXI.Text(`${index + 1}`, {
        fontSize: 10,
        fill: color,
        fontWeight: 'bold',
        fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
      });
      numText.anchor.set(0.5);
      numText.x = radius - 2;
      numText.y = -radius + 2;
      sprite.addChild(numText);

      // 玩家名（棋子下方）
      const nameLabel = new PIXI.Text(player.name, {
        fontSize: 10,
        fill: 0x5C4033,
        fontWeight: 'bold',
        fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
        align: 'center',
      });
      nameLabel.anchor.set(0.5, 0);
      nameLabel.y = radius + 3;
      sprite.addChild(nameLabel);

      // 错开多人位置
      const offsets = [
        { x: -26, y: -34 },
        { x: 26, y: -34 },
        { x: -26, y: 22 },
        { x: 26, y: 22 },
      ];
      const offset = offsets[index] || offsets[0];

      sprite.x = parentCell.x + inner / 2 + offset.x;
      sprite.y = parentCell.y + inner / 2 + offset.y;

      this.boardContainer.addChild(sprite);
      this.playerSprites.set(player.id, sprite);
    });
  }

  // ============ 玩家站立格子高亮 ============
  renderPlayerPositionHighlights(players: Player[]) {
    if (this._isDestroyed) return;

    // 清除旧高亮
    this.playerPositionHighlights.forEach(h => {
      h.destroy({ children: true });
      if (h.parent) h.parent.removeChild(h);
    });
    this.playerPositionHighlights.clear();

    const cs = this.cellSize;
    const activePlayers = players.filter(p => !p.bankrupt);

    // 统计每个格子上有哪几个玩家
    const cellPlayers = new Map<number, Player[]>();
    activePlayers.forEach(p => {
      if (!cellPlayers.has(p.position)) cellPlayers.set(p.position, []);
      cellPlayers.get(p.position)!.push(p);
    });

    cellPlayers.forEach((cellPlayersList, cellId) => {
      const pos = this.getCellPos(cellId);
      const highlight = new PIXI.Container();
      highlight.name = `pos_hl_${cellId}`;

      // 外圈发光环
      const outerGlow = new PIXI.Graphics();
      const playerIdx = players.findIndex(p => p.id === cellPlayersList[0].id);
      const color = COLORS.playerColors[playerIdx % 4];
      
      // 如果多人同格，用白色（所有颜色混合）
      const glowColor = cellPlayersList.length > 1 ? 0xFFFFFF : color;
      
      outerGlow.lineStyle(3, glowColor, 0.55);
      outerGlow.drawRoundedRect(pos.x + 2, pos.y + 2, cs - 4, cs - 4, 8);
      outerGlow.lineStyle(1.5, glowColor, 0.25);
      outerGlow.drawRoundedRect(pos.x - 2, pos.y - 2, cs + 4, cs + 4, 12);
      highlight.addChild(outerGlow);

      // 每个玩家的小箭头标记（格子四角）
      cellPlayersList.forEach((p, idx) => {
        const mark = new PIXI.Graphics();
        const pColor = COLORS.playerColors[players.findIndex(pl => pl.id === p.id) % 4];
        const positions = [
          { x: pos.x + 6, y: pos.y + 6 },
          { x: pos.x + cs - 14, y: pos.y + 6 },
          { x: pos.x + 6, y: pos.y + cs - 14 },
          { x: pos.x + cs - 14, y: pos.y + cs - 14 },
        ];
        const mp = positions[idx % 4];
        mark.beginFill(pColor, 0.8);
        mark.drawCircle(mp.x, mp.y, 5);
        mark.endFill();
        highlight.addChild(mark);
      });

      this.boardContainer.addChild(highlight);
      this.playerPositionHighlights.set(cellId, highlight);
    });
  }

  // ============ 渲染建筑（卡通风格） ============
  renderBuildings(properties: PropertyOwnership[], _board: BoardCell[]) {
    if (this._isDestroyed) return;

    this.buildingSprites.forEach(sprite => {
      sprite.destroy({ children: true });
    });
    this.buildingSprites.clear();

    properties.forEach(prop => {
      if (prop.buildingLevel <= 0) return;
      const parentCell = this.cellSprites.get(prop.cellId);
      if (!parentCell) return;
      const inner = this.cellSize - 6;

      const bldgTexture = this._buildingTextures.get(prop.buildingLevel);
      if (bldgTexture && bldgTexture.valid && !bldgTexture.destroyed) {
        // 使用 3D tile PNG
        const sprite = new PIXI.Sprite(bldgTexture);
        const size = inner * 0.45;
        sprite.width = size;
        sprite.height = size;
        sprite.anchor.set(0.5);
        sprite.x = parentCell.x + inner / 2;
        sprite.y = parentCell.y + inner - 12;
        this.boardContainer.addChild(sprite);
        this.buildingSprites.set(`b_${prop.cellId}`, sprite);
      } else {
        // fallback: 矢量绘制
        const building = new PIXI.Graphics();
        const level = prop.buildingLevel;

        if (level === 1) {
          // 🏠 可爱小房子
          building.beginFill(0xF4A950);
          building.drawRoundedRect(-12, -20, 24, 20, 4);
          building.endFill();
          building.beginFill(0xFF8C69);
          building.moveTo(-15, -20);
          building.lineTo(0, -33);
          building.lineTo(15, -20);
          building.closePath();
          building.endFill();
          building.beginFill(0xFFFFFF, 0.7);
          building.drawRoundedRect(-5, -24, 4, 4, 1);
          building.drawRoundedRect(2, -24, 4, 4, 1);
          building.endFill();
          building.beginFill(0xFFFFFF, 0.5);
          building.drawRoundedRect(-2, -12, 4, 7, 2);
          building.endFill();
        } else if (level === 2) {
          // 🏡 可爱小别墅
          building.beginFill(0x7EC8A4);
          building.drawRoundedRect(-14, -22, 28, 22, 5);
          building.endFill();
          building.beginFill(0x5CB88C);
          building.moveTo(-18, -22);
          building.lineTo(0, -38);
          building.lineTo(18, -22);
          building.closePath();
          building.endFill();
          building.beginFill(0xFFFFFF, 0.7);
          building.drawRoundedRect(-10, -26, 5, 5, 1);
          building.drawRoundedRect(6, -26, 5, 5, 1);
          building.drawRoundedRect(-2, -28, 5, 5, 1);
          building.endFill();
          building.beginFill(0xFFFFFF, 0.5);
          building.drawRoundedRect(-3, -10, 6, 8, 3);
          building.endFill();
        } else if (level === 3) {
          // 🏨 可爱大酒店
          building.beginFill(0xA29BFE);
          building.drawRoundedRect(-16, -14, 32, 14, 5);
          building.drawRoundedRect(-12, -32, 24, 18, 6);
          building.drawRoundedRect(-8, -48, 16, 16, 6);
          building.endFill();
          building.beginFill(0xFFFFFF, 0.7);
          building.drawRoundedRect(-9, -18, 5, 4, 1);
          building.drawRoundedRect(5, -18, 5, 4, 1);
          building.drawRoundedRect(-6, -36, 5, 5, 1);
          building.drawRoundedRect(2, -36, 5, 5, 1);
          building.drawRoundedRect(-2, -44, 5, 4, 1);
          building.endFill();
          // 金色星星
          building.beginFill(0xF4A950);
          drawStar(building, 0, -53, 4, 8, 5);
          building.endFill();
        }

        building.x = parentCell.x + inner / 2;
        building.y = parentCell.y + inner - 12;
        this.boardContainer.addChild(building);
        this.buildingSprites.set(`b_${prop.cellId}`, building);
      }
    });
  }

  // ============ 所有权标识（彩色边框+标签+等级⭐） ============
  renderOwnership(properties: PropertyOwnership[], players: Player[]) {
    if (this._isDestroyed) return;

    // 清除旧标记
    this.ownershipMarkers.forEach(m => {
      m.destroy({ children: true });
      if (m.parent) m.parent.removeChild(m);
    });
    this.ownershipMarkers.clear();

    properties.forEach(prop => {
      const parentCell = this.cellSprites.get(prop.cellId);
      if (!parentCell) return;
      const owner = players.find(p => p.id === prop.ownerId);
      if (!owner) return;

      const ownerIdx = players.findIndex(p => p.id === prop.ownerId);
      const color = COLORS.playerColors[ownerIdx % 4];
      const inner = this.cellSize - 6;
      const cs = this.cellSize;
      const pos = this.getCellPos(prop.cellId);

      // ⭐ 彩色高亮边框（比之前的细条更醒目）
      const border = new PIXI.Graphics();
      border.lineStyle(2.5, color, 0.7);
      border.drawRoundedRect(2, 2, cs - 4, cs - 4, 6);
      border.lineStyle(1, color, 0.3);
      border.drawRoundedRect(0, 0, cs, cs, 8);
      parentCell.addChild(border);
      this.ownershipMarkers.set(prop.cellId, border);

      // 底部彩色条（半透明，显示所有者颜色）
      const strip = new PIXI.Graphics();
      strip.beginFill(color, 0.9);
      strip.drawRoundedRect(3, inner - 8, inner - 6, 6, 3);
      strip.endFill();
      strip.beginFill(0xFFFFFF, 0.4);
      strip.drawRoundedRect(3, inner - 8, inner - 6, 2, 2);
      strip.endFill();
      parentCell.addChild(strip);
      this.ownershipMarkers.set(-prop.cellId - 10, strip);

      // LV 等级徽章（格子顶部，醒目展示土地等级）
      if (prop.buildingLevel > 0) {
        const lvColors: Record<number, number> = {
          1: 0x4CAF50, // 房子 — 绿色
          2: 0x2196F3, // 别墅 — 蓝色
          3: 0xFFD700, // 酒店 — 金色
        };
        const lvColor = lvColors[prop.buildingLevel] || 0xFFD700;
        const lvBadge = new PIXI.Graphics();
        // 半透明暖棕色底板
        lvBadge.beginFill(0x5C4033, 0.6);
        lvBadge.drawRoundedRect(3, 3, 40, 16, 8);
        lvBadge.endFill();
        // 右侧彩色方块装饰
        lvBadge.beginFill(lvColor, 0.9);
        lvBadge.drawRoundedRect(27, 3, 16, 16, 8);
        lvBadge.endFill();
        // 发光效果
        lvBadge.beginFill(lvColor, 0.3);
        lvBadge.drawRoundedRect(1, 1, 42, 20, 10);
        lvBadge.endFill();
        parentCell.addChild(lvBadge);
        this.ownershipMarkers.set(-prop.cellId - 20, lvBadge);

        const lvText = new PIXI.Text(`Lv.${prop.buildingLevel}`, {
          fontSize: 9,
          fill: 0xFFFFFF,
          fontWeight: 'bold',
          fontFamily: 'monospace',
        });
        lvText.anchor.set(0.5);
        lvText.x = 23;
        lvText.y = 11;
        parentCell.addChild(lvText);
        this.ownershipMarkers.set(-prop.cellId - 21, lvText as any);
      }

      // 所有者标签（右下角，显示 P{n}）
      const tag = new PIXI.Graphics();
      tag.beginFill(color, 0.9);
      tag.drawRoundedRect(inner - 36, inner - 24, 40, 18, 4);
      tag.endFill();
      
      const labelText = `P${ownerIdx + 1}`;
      const tagText = new PIXI.Text(labelText, {
        fontSize: 10,
        fill: 0xFFFFFF,
        fontWeight: 'bold',
        fontFamily: 'monospace',
      });
      tagText.anchor.set(0.5);
      tagText.x = inner - 18;
      tagText.y = inner - 15;
      parentCell.addChild(tag);
      parentCell.addChild(tagText);
      this.ownershipMarkers.set(-prop.cellId - 11, tag);
      this.ownershipMarkers.set(-prop.cellId - 12, tagText as any);
    });
    this._requestRender();
  }

  // ============ 瞬移（无动画，直接跳到目标格） ============
  teleportTo(playerId: string, cellId: number) {
    if (this._isDestroyed) return;
    const sprite = this.playerSprites.get(playerId);
    if (!sprite) return;

    const pos = this.getCellPos(cellId);
    const inner = this.cellSize - 6;
    const offset = this._getPlayerOffset(playerId);
    sprite.x = pos.x + inner / 2 + offset.x;
    sprite.y = pos.y + inner / 2 + offset.y;
    this._requestRender();
  }

  // ============ 步进动画（逐格移动+相机平移跟随） ============
  // 策略：focusOn 放大 → 每步棋子+相机同步移动 → resetView 缩小
  animateStepByStep(
    playerId: string,
    path: number[],
    onStep: (cellId: number) => void,
    onComplete: () => void,
  ) {
    if (this._isDestroyed) { onComplete(); return; }
    const sprite = this.playerSprites.get(playerId);
    if (!sprite || path.length === 0) { onComplete(); return; }

    this._currentPath = path;
    this._currentPathIndex = 0;

    const stepDuration = 140;
    let completed = false;

    // 🔑 先把棋子瞬移到起始格（renderPlayers 可能已经把它放在终点位置）
    const firstPos = this.getCellPos(path[0]);
    const inner = this.cellSize - 6;
    const offset = this._getPlayerOffset(playerId);
    sprite.x = firstPos.x + inner / 2 + offset.x;
    sprite.y = firstPos.y + inner / 2 + offset.y;

    // 聚焦起始格
    const fx = firstPos.x + inner / 2;
    const fy = firstPos.y + inner / 2;

    this.focusOn(fx, fy, 1.5, 300, () => {
      this._stepNext(playerId, path, 0, sprite!, stepDuration, onStep, () => {
        if (completed) return;
        completed = true;
        setTimeout(() => {
          this.resetView(500, onComplete);
        }, 400);
      });
    });
  }

  private _stepNext(
    playerId: string,
    path: number[],
    index: number,
    sprite: PIXI.Container,
    duration: number,
    onStep: (cellId: number) => void,
    onDone: () => void,
  ) {
    if (index >= path.length) { onDone(); return; }
    if (this._isDestroyed) { onDone(); return; }
    // 异步回调中 sprite 可能已被清理（focusOn 动画期间 React 重渲染/board 重初始化）
    if (!sprite || sprite.destroyed || !this.playerSprites.has(playerId)) { onDone(); return; }

    const cellId = path[index];
    const pos = this.getCellPos(cellId);
    const inner = this.cellSize - 6;

    // 棋子目标位置
    const offset = this._getPlayerOffset(playerId);
    const targetX = pos.x + inner / 2 + offset.x;
    const targetY = pos.y + inner / 2 + offset.y;
    const startX = sprite.x;
    const startY = sprite.y;

    // 相机目标位置：以目标格子中心居中
    const scale = this.boardContainer.scale.x;
    const camTargetX = this.currentWidth / 2 - (pos.x + inner / 2) * scale;
    const camTargetY = this.currentHeight / 2 - (pos.y + inner / 2) * scale;
    const camStartX = this.boardContainer.x;
    const camStartY = this.boardContainer.y;

    // === 行走帧动画：使用 walk.png 精灵表 ===
    const frames = this._walkTextures.get(playerId);
    let walkSprite: PIXI.Sprite | null = null;
    if (frames && frames.length === 4) {
      // 创建行走精灵，叠在玩家 sprite 上方
      walkSprite = new PIXI.Sprite(frames[0]);
      walkSprite.anchor.set(0.5, 0.7); // 底部锚点（对齐地面）
      walkSprite.scale.set(this.cellSize / 120 * 0.7); // 缩放到适当大小
      walkSprite.name = '__walk_anim';
      sprite.addChild(walkSprite);

      // 行走时隐藏矢量 body + face + nameLabel（保留 shadow/ring/badge）
      sprite.children.forEach((child: any) => {
        if (!child.name || child.name === '__walk_anim') return;
        // 隐藏 Graphics 类型的 body（前2-3个 graphics child）
        if (child instanceof PIXI.Graphics && child.name !== '__selection') {
          child.visible = false;
        }
        // 隐藏 nameLabel
        if (child instanceof PIXI.Text) {
          child.visible = false;
        }
      });
    }

    let frameIdx = 0;
    let lastFrameTime = performance.now();
    const frameInterval = 120; // 每 120ms 切换一帧 (约 8fps)

    const startTime = performance.now();
    let stepDone = false;

    const animate = (now: number) => {
      if (this._isDestroyed || stepDone) return;
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - t, 2);

      // 棋子移动
      sprite.x = startX + (targetX - startX) * ease;
      sprite.y = startY + (targetY - startY) * ease - Math.sin(t * Math.PI) * 16;

      // 行走帧切换
      if (walkSprite && frames) {
        if (now - lastFrameTime >= frameInterval) {
          frameIdx = (frameIdx + 1) % 4;
          walkSprite.texture = frames[frameIdx];
          lastFrameTime = now;
        }
        // 左右翻转：根据移动方向
        const dx = targetX - startX;
        walkSprite.scale.x = Math.abs(walkSprite.scale.x) * (dx >= 0 ? 1 : -1);
      }

      // 相机同步平移（同一 ease，同一 RAF）
      this.boardContainer.x = camStartX + (camTargetX - camStartX) * ease;
      this.boardContainer.y = camStartY + (camTargetY - camStartY) * ease;

      // 手动触发渲染（autoStart: false 后必须手动 render，否则动画不可见）
      this.app.renderer.render(this.app.stage);

      if (t >= 1) {
        sprite.x = targetX;
        sprite.y = targetY;
        this.boardContainer.x = camTargetX;
        this.boardContainer.y = camTargetY;
        stepDone = true;

        // 移除行走精灵，恢复矢量 sprite
        if (walkSprite) {
          sprite.removeChild(walkSprite);
          walkSprite.destroy();
          this._walkSprites.delete(playerId);
          // 恢复所有隐藏的子元素
          sprite.children.forEach((child: any) => {
            if (child instanceof PIXI.Graphics || child instanceof PIXI.Text) {
              child.visible = true;
            }
          });
        }

        try { onStep(cellId); } catch (_) {}

        setTimeout(() => {
          this._stepNext(playerId, path, index + 1, sprite, duration, onStep, onDone);
        }, 50);
        return;
      }
      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }

  // 获取玩家在格内的固定偏移（纯基于玩家序号，多玩家同格时分散）
  private _getPlayerOffset(playerId: string): { x: number; y: number } {
    const playerIndex = Array.from(this.playerSprites.keys()).indexOf(playerId);
    const offsets = [
      { x: -24, y: -30 },
      { x: 24, y: -30 },
      { x: -24, y: 18 },
      { x: 24, y: 18 },
    ];
    return offsets[Math.max(0, Math.min(playerIndex, offsets.length - 1))] || offsets[0];
  }

  // 兼容旧的 animateMove（单步跳转）
  animateMove(playerId: string, fromCellId: number, toCellId: number, onComplete: () => void) {
    // 计算路径
    const path: number[] = [];
    let current = fromCellId;
    while (current !== toCellId) {
      current = (current + 1) % 40;
      path.push(current);
    }
    this.animateStepByStep(playerId, path, () => {}, onComplete);
  }

  // ============ 坐标工具 ============
  getCellPos(index: number): { x: number; y: number } {
    const cs = this.cellSize;
    if (index === 0) return { x: 10 * cs, y: 10 * cs };
    if (index <= 10) return { x: (10 - index) * cs, y: 10 * cs };
    if (index <= 20) return { x: 0, y: (20 - index) * cs };
    if (index <= 30) return { x: (index - 20) * cs, y: 0 };
    return { x: 10 * cs, y: (index - 30) * cs };
  }

  /** 屏幕坐标 → 格子 index（用于点击检测），未命中返回 -1 */
  private _screenToCell(screenX: number, screenY: number): number {
    const scale = this.boardContainer.scale.x;
    // 屏幕 → 棋盘世界坐标
    const worldX = (screenX - this.boardContainer.x) / scale;
    const worldY = (screenY - this.boardContainer.y) / scale;
    const cs = this.cellSize;
    const halfCS = cs / 2;
    const pad = 8; // 命中容差

    for (let i = 0; i < 40; i++) {
      const pos = this.getCellPos(i);
      if (
        worldX >= pos.x - pad && worldX <= pos.x + cs + pad &&
        worldY >= pos.y - pad && worldY <= pos.y + cs + pad
      ) {
        return i;
      }
    }
    return -1;
  }

  /** 获取格子的世界坐标中心 */
  getCellWorldCenter(cellId: number): { x: number; y: number } {
    const pos = this.getCellPos(cellId);
    const inner = this.cellSize - 6;
    return { x: pos.x + inner / 2, y: pos.y + inner / 2 };
  }

  destroy() {
    this._isDestroyed = true;
    cancelAnimationFrame(this._animRaf);
    this._stopRenderLoop();
    this._animTasks = [];
    // 清理行走纹理缓存
    this._walkTextures.clear();
    this._walkSprites.clear();
    this._walkLoading.clear();
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      this._onVisibilityChange = null;
    }
    try {
      this.app.destroy(true, { children: true, texture: true });
    } catch (e) { /* ignore */ }
  }
}
